import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Meta from "../../components/dynamicMeta";
import MarkdownRenderer from '../../components/MarkdownRenderer';
import { localizeDomain } from '../../utils/localizeDomain';
import ArticleCard from './ArticleCard';
import NavArticle from './Article_nav';

function Article() {
  const { lang, category, subcategory, nestedSubcategory, article } = useParams();
  const isApiCategory = category === 'api';
  const { t: tArticle } = useTranslation('article');
  const { t: tHome } = useTranslation('home');

  // Construir la clave del artículo directamente con los slugs de la URL
  let articleKey;
  if (nestedSubcategory) {
    articleKey = `${category}.${subcategory}.${nestedSubcategory}.${article}`;
  } else if (subcategory) {
    articleKey = `${category}.${subcategory}.${article}`;
  } else {
    articleKey = `${category}.${article}`;
  }

  const articleContent = tArticle(articleKey, { returnObjects: true });

  // Debug: verificar claves
  console.log('🔍 Debug Article Component:');
  console.log('- URL params:', { lang, category, subcategory, article });
  console.log('- Article key:', articleKey);
  console.log('- Article content found:', !!articleContent);
  console.log('- Has sections:', !!articleContent?.sections);
  if (articleContent?.sections?.[0]) {
    console.log('- First section keys:', Object.keys(articleContent.sections[0]));
  }

  if (!articleContent || !articleContent.sections) {
    console.log('❌ Article not found for key:', articleKey);
    return <p className="text-center text-gray-700 dark:text-gray-300">Loading...</p>;
  }

  // Obtener categorías desde home.json
  const categories = tHome('categories', { returnObjects: true }) || [];

  // Buscar la categoría actual
  const currentCategory = categories.find((cat) => cat.slug === category);
  let allArticles = [];

  if (currentCategory) {
    if (nestedSubcategory) {
      // Navegación de 3 niveles: category -> subcategory -> nestedSubcategory -> articles
      const currentSub = currentCategory.subcategories?.find((sub) => sub.slug === subcategory);
      const currentNestedSub = currentSub?.subcategories?.find((nested) => nested.slug === nestedSubcategory);
      allArticles = currentNestedSub?.articles || [];
    } else if (subcategory) {
      // Navegación de 2 niveles: category -> subcategory -> articles
      const currentSub = currentCategory.subcategories?.find((sub) => sub.slug === subcategory);
      allArticles = currentSub?.articles || [];
    } else {
      // Navegación de 1 nivel: category -> articles
      allArticles = currentCategory.articles || [];
    }
  }

  // Artículos recomendados
  const recommendedArticles = allArticles
    .filter((art) => art.slug !== article)
    .sort(() => 0.5 - Math.random())
    .slice(0, 4)
    .map((art) => {
      let link;
      if (nestedSubcategory) {
        link = `/${lang}/${category}/${subcategory}/${nestedSubcategory}/${art.slug}`;
      } else if (subcategory) {
        link = `/${lang}/${category}/${subcategory}/${art.slug}`;
      } else {
        link = `/${lang}/${category}/${art.slug}`;
      }
      return {
        title: art.title,
        link,
      };
    });

  return (
    <div>
      <Meta />
      <div className="prose mx-auto pb-6 sm:pb-10 dark:prose-invert">
        {/* Título */}
        <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">
          {localizeDomain(articleContent.title)}
        </h1>

        {/* Secciones */}
        {articleContent.sections.map((section, index) => {
          // Claves separadas: normales vs fórmulas
          const entries = Object.entries(section);

          const normalKeys = entries
            .map(([k]) => k)
            .filter((key) =>
              // Todo lo "normal", EXCLUYENDO fórmulas
              (['content', 'box', 'greenbox', 'redbox', 'content_list', 'content_list_no', 'box-details', 'img']
                .some((prefix) => key.startsWith(prefix))) && !key.startsWith('formula')
            );

          // Fórmulas: cualquier clave que empiece con "formula"
          const formulaEntries = entries.filter(([k]) => k.startsWith('formula'));

          // Desduplicar fórmulas por VALOR
          const seenFormulaValues = new Set();
          const uniqueFormulaEntries = formulaEntries.filter(([_, val]) => {
            const v = String(val);
            if (seenFormulaValues.has(v)) return false;
            seenFormulaValues.add(v);
            return true;
          });

          return (
            <div key={index} className="mb-8">
              {section.subtitle && (
                <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
                  {localizeDomain(section.subtitle)}
                </h2>
              )}

              {/* Detectar si es sección de Responses en cualquier idioma */}
              {section.subtitle && (
                section.subtitle.toLowerCase().includes('response') || // English
                section.subtitle.toLowerCase().includes('respuesta') || // Spanish
                section.subtitle.toLowerCase().includes('resposta') || // Portuguese
                section.subtitle.toLowerCase().includes('réponse') || // French
                section.subtitle.toLowerCase().includes('risposte') || // Italian
                section.subtitle.toLowerCase().includes('響應') || // Chinese (Traditional)
                section.subtitle.toLowerCase().includes('回应') || // Chinese (Simplified)
                section.subtitle.toLowerCase().includes('回應') // Chinese (Cantonese)

              ) ? (
                <ResponseTabs
                  section={section}
                  keys={normalKeys} // NO pasamos fórmulas a tabs
                />
              ) : (
                <>
                  {/* Render normal (sin fórmulas) */}
                  {normalKeys.map((key, i) => {
                    const value = section[key];

                    if (key.startsWith('greenbox')) {
                      const [firstLine, ...restLines] = String(value).split('\n');
                      const rest = restLines.join('\n');

                      return (
                        <div
                          key={i}
                          className="mb-4 border-l-4 border-green-500 dark:text-green-100 bg-gray-50 dark:bg-grayButton rounded"
                        >
                          <div className="p-4 font-semibold">{firstLine}</div>
                          <details className="px-4 pb-4">
                            <summary className="cursor-pointer text-green-700 dark:text-green-300 hover:underline mb-2">
                              Show details
                            </summary>
                            <div className="pt-2 prose prose-p:my-0 marker:text-green-500 dark:marker:text-green-300 dark:prose-invert">
                              <MarkdownRenderer content={rest} context="greenbox" />
                            </div>
                          </details>
                        </div>
                      );
                    }

                    if (key.startsWith('redbox')) {
                      const [firstLine, ...restLines] = String(value).split('\n');
                      const rest = restLines.join('\n');

                      return (
                        <div
                          key={i}
                          className="mb-4 border-l-4 border-red-500 dark:text-red-100 bg-gray-50 dark:bg-grayButton rounded"
                        >
                          <div className="p-4 font-semibold">{firstLine}</div>
                          <details className="px-4 pb-4">
                            <summary className="cursor-pointer text-red-700 dark:text-red-300 hover:underline mb-2">
                              Show details
                            </summary>
                            <div className="pt-2 prose prose-p:my-0 marker:text-red-600 dark:marker:text-red-400 dark:prose-invert">
                              <MarkdownRenderer content={rest} context="redbox" />
                            </div>
                          </details>
                        </div>
                      );
                    }

                    if (key.startsWith('box')) {
                      const valueStr = String(value).trim();
                      const isOnlyCodeBlock =
                        valueStr.startsWith('```') && valueStr.endsWith('```');
                      const lines = valueStr.split('\n');
                      const firstLine = lines[0];
                      const rest = lines.slice(1).join('\n').trim();
                      const hasDetails = rest.length > 0;

                      return (
                        <div key={i} className="bg-gray-50 dark:bg-grayButton rounded-lg">
                          {/* caso: solo bloque de código completo */}
                          {isOnlyCodeBlock ? (
                            <div className="p-4 prose prose-p:my-0 dark:prose-invert">
                              <MarkdownRenderer content={valueStr} context="box" />
                            </div>
                          ) : hasDetails ? (
                            <>
                              <div className="p-4 text-gray-800 dark:text-gray-100">
                                <MarkdownRenderer content={firstLine} />
                              </div>
                              <div className="px-4 pb-4 prose prose-p:my-0 dark:prose-invert">
                                <MarkdownRenderer content={rest} />
                              </div>
                            </>
                          ) : (
                            // solo una línea sin detalles
                            <div className="p-4 text-gray-800 dark:text-gray-100">
                              <MarkdownRenderer content={valueStr} />
                            </div>
                          )}
                        </div>
                      );
                    }

                    if (key.startsWith('content_list_no')) {
                      return (
                        <div
                          key={i}
                          className="mt-[-0.8rem] p-3 bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-400 rounded"
                        >
                          <div className="prose prose-p:my-1 prose-ul:my-2 dark:prose-invert">
                            <MarkdownRenderer content={String(value)} context="content_list_no" />
                          </div>
                        </div>
                      );
                    }

                    if (key.startsWith('content_list')) {
                      return (
                        <div key={i}>
                          <div className="prose prose-p:my-1 prose-ul:my-2 dark:prose-invert">
                            <MarkdownRenderer content={String(value)} context="content_list" />
                          </div>
                        </div>
                      );
                    }

                    if (key.startsWith('box-details')) {
                      const valueStr = String(value).trim();
                      const isOnlyCodeBlock =
                        valueStr.startsWith('```') && valueStr.endsWith('```');
                      const lines = valueStr.split('\n');
                      const firstLine = lines[0];
                      const rest = lines.slice(1).join('\n').trim();
                      const hasDetails = rest.length > 0;

                      return (
                        <div
                          key={i}
                          className="mb-4 border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded"
                        >
                          {/* caso: solo bloque de código completo */}
                          {isOnlyCodeBlock ? (
                            <div className="p-4 prose prose-p:my-0 dark:prose-invert">
                              <MarkdownRenderer content={valueStr} context="box" />
                            </div>
                          ) : hasDetails ? (
                            <>
                              <div className="p-4 text-gray-800 dark:text-gray-100">
                                <MarkdownRenderer content={firstLine} />
                              </div>
                              <details className="px-4 pb-4">
                                <summary className="cursor-pointer text-gray-600 dark:text-gray-300 hover:underline mb-2">
                                  Show details
                                </summary>
                                <div className="pt-2 prose prose-p:my-0 dark:prose-invert">
                                  <MarkdownRenderer content={rest} />
                                </div>
                              </details>
                            </>
                          ) : (
                            // solo una línea sin detalles
                            <div className="p-4 text-gray-800 dark:text-gray-100">
                              <MarkdownRenderer content={valueStr} />
                            </div>
                          )}
                        </div>
                      );
                    }

                    // Manejo de imágenes
                    if (key.startsWith('img')) {
                      return (
                        <div key={i} className="my-4 text-center">
                          <img
                            src={String(value)}
                            alt={section.subtitle || section.title || 'Image'}
                            className="max-w-full h-auto rounded-lg shadow-md mx-auto"
                          />
                        </div>
                      );
                    }

                    // Por defecto: contenido normal
                    return <MarkdownRenderer key={i} content={String(value)} context="default" />;
                  })}

                  {/* Fórmulas: SOLO aquí (desduplicadas por valor) */}
                  {uniqueFormulaEntries.map(([k, rawValue], i) => {
                    const value = String(rawValue);
                    // Si ya contiene $ o $$, usar tal como está, sino envolver en $$
                    const formulaContent = value.includes('$') ? value : `$$${value.replace(/[_]/g, '\\_')}$$`;
                    return (
                      <div
                        key={`formula-${i}`}
                        className="mt-[-0.8rem] p-3 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded text-center"
                      >
                        <div className="prose dark:prose-invert">
                          <MarkdownRenderer content={formulaContent} context="formula" />
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}

        {/* Recomendados */}
        {recommendedArticles.length > 0 && (
          <div className="mt-10">
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 dark:text-gray-200">
              {tArticle('articleRecommended')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {recommendedArticles.map((art, index) => (
                <ArticleCard key={index} {...art} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Componente para mostrar responses como tabs
function ResponseTabs({ section, keys }) {
  const [activeTab, setActiveTab] = useState(0);

  console.log('📊 ResponseTabs Debug:');
  console.log('- Section subtitle:', section.subtitle);
  console.log('- Available keys:', keys);
  console.log('- Section keys:', Object.keys(section));

  const responses = keys
    .filter((key) =>
      key.startsWith('box') ||
      key.startsWith('greenbox') ||
      key.startsWith('redbox') ||
      /^redbox\d+$/.test(key) // Incluir redbox1, redbox2, etc.
    )
    .map((key, index) => {
      const value = section[key];
      const firstLine = String(value).split('\n')[0];
      const codeMatch = firstLine.match(/\d{3}/);
      const errorCode = codeMatch ? codeMatch[0] : `${200 + index}`;

      let type = 'default';
      if (key.startsWith('greenbox')) type = 'success';
      else if (key.startsWith('redbox')) type = 'error';
      else if (key.startsWith('box')) type = 'default';

      return { key, value, errorCode, type };
    });

  const getTabStyles = (type, isActive) => {
    const baseStyles = 'py-2 px-3 border-b-2 font-medium text-sm rounded-t-lg';

    if (isActive) {
      switch (type) {
        case 'success':
          return `${baseStyles} border-green-500 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20`;
        case 'error':
          return `${baseStyles} border-red-500 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20`;
        default:
          return `${baseStyles} border-blue-500 text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20`;
      }
    } else {
      return `${baseStyles} border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300`;
    }
  };

  const getContentStyles = (type) => {
    switch (type) {
      case 'success':
        return 'bg-gray-50 dark:bg-grayButton border-l-4 border-green-500';
      case 'error':
        return 'bg-gray-50 dark:bg-grayButton border-l-4 border-red-500';
      default:
        return 'bg-gray-50 dark:bg-grayButton';
    }
  };

  return (
    <div className="mb-8">
      {/* Tabs Header */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="flex space-x-2">
          {responses.map((response, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className={getTabStyles(response.type, activeTab === index)}
            >
              {response.errorCode}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-4">
        {responses[activeTab] && (
          <div className={`rounded-lg p-4 ${getContentStyles(responses[activeTab].type)}`}>
            <MarkdownRenderer content={responses[activeTab].value} context="response" />
          </div>
        )}
      </div>
    </div>
  );
}

export default Article;
